import type {
  TFeeComponentApi,
  TStudentCategory,
  TCategoryComponentAttachment,
  TGetClassFeesResponse,
} from "../../apis/feeStructure/feeStructure.interfaces";

const instituteKey = "institute-1";

let feeComponentIdSeq = 10;
let categoryIdSeq = 10;
let ruleIdSeq = 10;

/** Mutable institute-scoped fee components (reusable types) */
export let feeComponentsMock: TFeeComponentApi[] = [
  {
    id: "fc-tuition",
    name: "Tuition Fee",
    feeType: "tuition",
    description: "Base tuition",
    amount: 5000,
    currency: "INR",
    frequency: "monthly",
    isActive: true,
    status: "active",
    instituteId: instituteKey,
    createdAt: new Date().toISOString(),
  },
  {
    id: "fc-admission",
    name: "Admission Fee",
    feeType: "admission",
    description: "One-time admission",
    amount: 10000,
    currency: "INR",
    frequency: "one-time",
    isActive: true,
    status: "active",
    instituteId: instituteKey,
    createdAt: new Date().toISOString(),
  },
];

/** Student categories per class id */
export let studentCategoriesByClassMock: Record<string, TStudentCategory[]> = {
  "class-1": [
    {
      id: "cat-general",
      instituteId: instituteKey,
      classId: "class-1",
      name: "General",
      description: "Default bucket",
      isDefault: true,
      isActive: true,
      createdAt: new Date().toISOString(),
    },
  ],
};

/** Category-component rules */
export let categoryAttachmentsMock: Record<string, TCategoryComponentAttachment[]> =
  {
    "cat-general": [
      {
        id: "rule-tuition-gen",
        categoryId: "cat-general",
        componentTypeId: "fc-tuition",
        ruleType: "override_amount",
        value: 5000,
        isActive: true,
        priority: 0,
        componentType: {
          id: "fc-tuition",
          code: "tuition",
          name: "Tuition Fee",
          amount: 5000,
          currencyCode: "INR",
          isActive: true,
        },
        createdAt: new Date().toISOString(),
      },
    ],
  };

export function nestedForComponent(
  fc: TFeeComponentApi,
): TCategoryComponentAttachment["componentType"] {
  return {
    id: fc.id,
    code: fc.feeType,
    name: fc.name,
    description: fc.description,
    amount: fc.amount,
    currencyCode: fc.currency,
    isActive: fc.isActive,
  };
}

export function buildClassFeesMock(params: {
  classId?: string;
  academicYear?: string;
  instituteId: string;
}): TGetClassFeesResponse {
  if (!params.instituteId) {
    return { classes: [] };
  }

  const classesOut: TGetClassFeesResponse["classes"] = [];

  const classMeta: Record<
    string,
    { name: string; grade: string; academicYear: string }
  > = {
    "class-1": {
      name: "Grade cohort — 2024-25",
      grade: "1",
      academicYear: "2024-2025",
    },
    "class-2": {
      name: "Grade cohort — 2024-25",
      grade: "2",
      academicYear: "2024-2025",
    },
    "class-3": {
      name: "Senior cohort — 2024-25",
      grade: "10",
      academicYear: "2024-2025",
    },
  };

  const classIds = params.classId
    ? [params.classId]
    : Object.keys(classMeta);

  for (const cid of classIds) {
    const meta = classMeta[cid];
    if (!meta) continue;
    if (
      params.academicYear &&
      meta.academicYear !== params.academicYear
    ) {
      continue;
    }

    const cats = studentCategoriesByClassMock[cid] ?? [];
    const categories = cats.map((c) => {
      const attachments = categoryAttachmentsMock[c.id] ?? [];
      const feeComponents = attachments.map((a) => ({
        feeId: a.id,
        feeAmount:
          a.ruleType === "waive"
            ? 0
            : a.ruleType === "override_amount" && a.value != null
              ? a.value
              : a.componentType.amount ?? 0,
        feeType: a.componentType.code ?? "other",
        feeDescription: a.componentType.name ?? a.componentType.code,
        isOptional: false,
      }));

      return {
        categoryId: c.id,
        category: c.name,
        categoryDescription: c.description ?? "",
        feeComponents,
        requiredDocuments: (c.requiredDocumentTypes ?? []).map((code) => ({
          documentId: code,
          documentType: code,
          documentDescription: "",
        })),
      };
    });

    classesOut.push({
      classId: cid,
      className: meta.name,
      classDescription: `${meta.grade} · ${meta.academicYear}`,
      categories,
    });
  }

  return { classes: classesOut };
}

export function pushFeeComponentMock(row: TFeeComponentApi) {
  feeComponentsMock.push(row);
}

export function upsertStudentCategoriesForClass(classId: string, rows: TStudentCategory[]) {
  studentCategoriesByClassMock[classId] = rows;
}

export function setAttachmentsForCategory(
  categoryId: string,
  rows: TCategoryComponentAttachment[],
) {
  categoryAttachmentsMock[categoryId] = rows;
}

export function nextFeeComponentId() {
  feeComponentIdSeq += 1;
  return `fc-mock-${feeComponentIdSeq}`;
}

export function nextCategoryId() {
  categoryIdSeq += 1;
  return `cat-mock-${categoryIdSeq}`;
}

export function nextRuleId() {
  ruleIdSeq += 1;
  return `rule-mock-${ruleIdSeq}`;
}
